import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toastr from "toastr";
import {
  fetchBookingWorkerRequests,
  fetchClientInviteListWorkersSession,
  fetchClientSuggestedWorkers,
  fetchClientSuggestedWorkersSession,
  useInviteListAppend,
  useInviteListRemove,
  useManualInviteWorkers,
  useRejectClientWorker,
} from "../../../../api/booking";
import { getShiftInviteCap } from "../inviteCaps";

export const SUGGESTED_WORKERS_PAGE_LIMIT = 10;

/** Stable key for per-button loading (shift + worker slug). */
export function getStep1ActionKey(shiftIndex, workerSlug) {
  return `${shiftIndex}:${workerSlug}`;
}

export function normalizeClientBookingPayload(res) {
  return res?.data?.data ?? res?.data ?? res;
}

/** Reads `smartMatchingStartTime` from suggested-workers or suggested-workers-session API payloads. */
export function getSmartMatchingStartTimeFromResponse(res) {
  const payload = normalizeClientBookingPayload(res);
  if (!payload?.smartMatchingStartTime) return null;
  const v = payload.smartMatchingStartTime;
  if (typeof v === "string") return v;
  if (v instanceof Date) return v.toISOString();
  return String(v);
}

/** TTL in minutes from suggested-workers / suggested-workers-session payload (when booking omits it). */
function getSessionTtlMinutesFromSuggestedPayload(payload) {
  if (!payload) return undefined;
  if (typeof payload.suggestedWorkersSessionTtlMinutes === "number") {
    return payload.suggestedWorkersSessionTtlMinutes;
  }
  if (typeof payload.sessionTtlSeconds === "number") {
    return Math.round(payload.sessionTtlSeconds / 60);
  }
  return undefined;
}

export function getWorkerSlug(worker) {
  if (!worker) return null;
  return (
    worker.user?.slug ||
    worker.slug ||
    worker.workerSlug ||
    (worker.workerId != null ? String(worker.workerId) : null)
  );
}

export function getWorkerDisplayName(worker) {
  return worker?.user?.fullName || worker?.fullName || "Worker";
}

export function getWorkerAvailableDates(worker) {
  const raw = worker?.availableDates;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d) => (typeof d === "string" ? d : d?.date))
    .filter(Boolean);
}

function getSuggestedWorkerArray(payload) {
  if (!payload || payload.shiftPattern) return null;
  const list = payload.suggestedWorkers ?? payload.workers;
  return Array.isArray(list) ? list : null;
}

/**
 * Supports:
 * - New suggested-workers API: `{ suggestedWorkers | workers, totalRecords, results, sessionId, shift, ... }` (no shiftPattern)
 * - Legacy booking-shaped API: `{ shiftPattern: [{ workers, suggestedWorkersMatchingStatus, hasMore }] }`
 */
export function extractShiftSlice(
  res,
  shiftIndex,
  { inferHasMoreFromChunkSize = false } = {}
) {
  const payload = normalizeClientBookingPayload(res);
  if (!payload) {
    return {
      workers: [],
      hasMore: false,
      matchingStatus: null,
      totalRecords: undefined,
    };
  }

  const flat = getSuggestedWorkerArray(payload);
  if (flat) {
    const workers = flat;
    const totalRecords =
      typeof payload.totalRecords === "number" ? payload.totalRecords : undefined;
    let hasMore = false;
    if (totalRecords != null) {
      hasMore = workers.length < totalRecords;
    } else if (payload.hasMore === true) hasMore = true;
    else if (payload.hasMore === false) hasMore = false;
    else if (inferHasMoreFromChunkSize) hasMore = workers.length >= SUGGESTED_WORKERS_PAGE_LIMIT;
    return {
      workers,
      hasMore,
      matchingStatus: payload.suggestedWorkersMatchingStatus ?? payload.matchingStatus ?? null,
      totalRecords,
    };
  }

  const pat = payload.shiftPattern?.[shiftIndex];
  if (!pat) {
    return { workers: [], hasMore: false, matchingStatus: null, totalRecords: undefined };
  }
  const workers = pat.workers ?? [];
  let hasMore = false;
  if (pat.hasMore === true) hasMore = true;
  else if (pat.hasMore === false) hasMore = false;
  else if (inferHasMoreFromChunkSize) hasMore = workers.length >= SUGGESTED_WORKERS_PAGE_LIMIT;
  else hasMore = false;
  return {
    workers,
    hasMore,
    matchingStatus: pat.suggestedWorkersMatchingStatus ?? null,
    totalRecords: undefined,
  };
}

export function dedupeWorkers(workers) {
  const seen = new Set();
  const out = [];
  for (const w of workers) {
    const slug = getWorkerSlug(w);
    const key = slug ?? JSON.stringify(w);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(w);
  }
  return out;
}

export function buildEmptyInviteListMap(patternsLen) {
  const map = {};
  for (let i = 0; i < patternsLen; i++) {
    map[i] = { workerAlreadyInvited: false, workers: [] };
  }
  return map;
}

export function applyShiftsSnapshotToInviteMap(shifts, patternsLen) {
  const map = {};
  for (const s of shifts || []) {
    map[s.shiftIndex] = {
      workerAlreadyInvited: !!s.workerAlreadyInvited,
      workers: Array.isArray(s.workers) ? s.workers : [],
    };
  }
  for (let i = 0; i < patternsLen; i++) {
    if (!map[i]) map[i] = { workerAlreadyInvited: false, workers: [] };
  }
  return map;
}

export function parseWorkerRequestsPage(res) {
  if (!res || typeof res !== "object") return { items: [], total: 0 };
  if (Array.isArray(res.data)) {
    const items = res.data;
    const total = typeof res.total === "number" ? res.total : items.length;
    return { items, total };
  }
  const root = res?.data?.data ?? res?.data ?? res;
  if (Array.isArray(root)) {
    const total = typeof res.total === "number" ? res.total : root.length;
    return { items: root, total };
  }
  const items = Array.isArray(root?.data) ? root.data : [];
  const total = typeof root?.total === "number" ? root.total : items.length;
  return { items, total };
}

function useInvitedWorkerRequestsInfinite(bookingSlug, enabled = true) {
  return useInfiniteQuery({
    queryKey: ["booking-worker-requests", bookingSlug, "invited", 10],
    queryFn: ({ pageParam }) =>
      fetchBookingWorkerRequests({
        bookingSlug,
        filter: "invited",
        page: pageParam,
        limit: 10,
      }),
    initialPageParam: 1,
    enabled: !!bookingSlug && enabled,
    getNextPageParam: (lastPage, allPages) => {
      const { items: lastItems, total } = parseWorkerRequestsPage(lastPage);
      const loaded = allPages.reduce(
        (sum, p) => sum + parseWorkerRequestsPage(p).items.length,
        0
      );
      if (!lastItems.length || loaded >= total) return undefined;
      return allPages.length + 1;
    },
  });
}

/**
 * Step 1: suggested workers, invite list (append/remove), manual invite, reject per shift.
 */
export function useStep1SuggestedWorkers({
  booking,
  bookingSlug,
  onAfterInvite,
  onSmartMatchingScheduleKnown,
}) {
  const rejectMut = useRejectClientWorker();
  const inviteMut = useManualInviteWorkers();
  const appendMut = useInviteListAppend();
  const removeMut = useInviteListRemove();

  const onScheduleNotifyRef = useRef(onSmartMatchingScheduleKnown);
  useEffect(() => {
    onScheduleNotifyRef.current = onSmartMatchingScheduleKnown;
  }, [onSmartMatchingScheduleKnown]);

  const [bootstrapKey, setBootstrapKey] = useState(0);
  const [byShift, setByShift] = useState({});
  const [selected, setSelected] = useState({});
  const [loadMorePending, setLoadMorePending] = useState({});
  const [initialPending, setInitialPending] = useState({});
  const [inviteListByShift, setInviteListByShift] = useState(null);
  const [inviteListSessionError, setInviteListSessionError] = useState(false);
  const [pendingAppendKey, setPendingAppendKey] = useState(null);
  const [pendingRejectKey, setPendingRejectKey] = useState(null);
  const [pendingRemoveKey, setPendingRemoveKey] = useState(null);
  const [firstInviteLocked, setFirstInviteLocked] = useState(false);
  const isFirstInvited = booking?.isFirstInvited === true || firstInviteLocked;

  const byShiftRef = useRef({});
  useEffect(() => {
    byShiftRef.current = byShift;
  }, [byShift]);

  /** Always read latest booking inside effects without listing `booking` as a dependency (avoids re-running on refetch / new object identity). */
  const bookingRef = useRef(booking);
  bookingRef.current = booking;

  const initDoneRef = useRef(new Set());

  const resetInitForBootstrap = useCallback(() => {
    initDoneRef.current = new Set();
  }, []);

  useEffect(() => {
    resetInitForBootstrap();
  }, [bootstrapKey, resetInitForBootstrap]);

  useEffect(() => {
    setPendingAppendKey(null);
    setPendingRejectKey(null);
    setPendingRemoveKey(null);
  }, [bootstrapKey]);

  const patternsLen = booking?.shiftPattern?.length ?? 0;
  const inviteListWorkerSessionId = booking?.inviteListWorkerSessionId;

  useEffect(() => {
    if (booking?.isFirstInvited === true) {
      setFirstInviteLocked(true);
    }
  }, [booking?.isFirstInvited]);

  const markInitialPending = useCallback((shiftIndex, pending) => {
    setInitialPending((prev) => ({ ...prev, [shiftIndex]: pending }));
  }, []);

  const applyShiftSlice = useCallback((shiftIndex, res, { append } = { append: false }) => {
    const schedule = getSmartMatchingStartTimeFromResponse(res);
    if (schedule) {
      const payload = normalizeClientBookingPayload(res);
      const sessionTtlMinutes = getSessionTtlMinutesFromSuggestedPayload(payload);
      onScheduleNotifyRef.current?.({
        smartMatchingStartTime: schedule,
        sessionTtlMinutes,
      });
    }

    const slice = extractShiftSlice(res, shiftIndex, {
      inferHasMoreFromChunkSize: append,
    });
    const { workers: chunk, matchingStatus, totalRecords } = slice;
    let { hasMore } = slice;

    setByShift((prev) => {
      const prevRow = prev[shiftIndex] || {};
      const base = append ? (prevRow.workers ?? []) : [];
      const merged = dedupeWorkers([...base, ...chunk]);
      if (totalRecords != null) {
        hasMore = merged.length < totalRecords;
      }
      return {
        ...prev,
        [shiftIndex]: {
          workers: merged,
          hasMore,
          matchingStatus: matchingStatus ?? prevRow.matchingStatus ?? null,
          initialLoaded: true,
        },
      };
    });
  }, []);

  const fetchSuggestedPage = useCallback(
    (shiftIndex, { append }) => {
      const req = append
        ? fetchClientSuggestedWorkers({
            bookingSlug,
            shiftIndex,
            limit: SUGGESTED_WORKERS_PAGE_LIMIT,
          })
        : fetchClientSuggestedWorkers({ bookingSlug, shiftIndex });

      req
        .then((res) => {
          applyShiftSlice(shiftIndex, res, { append });
          if (!append) markInitialPending(shiftIndex, false);
        })
        .catch(() => {
          if (!append) {
            markInitialPending(shiftIndex, false);
            setByShift((prev) => ({
              ...prev,
              [shiftIndex]: {
                ...(prev[shiftIndex] || {}),
                workers: [],
                hasMore: false,
                initialLoaded: true,
                suggestedFetchError: true,
              },
            }));
            toastr.error("Could not load suggested workers.");
          } else {
            toastr.error("Could not load more workers.");
          }
        })
        .finally(() => {
          if (append) {
            setLoadMorePending((p) => ({ ...p, [shiftIndex]: false }));
          }
        });
    },
    [applyShiftSlice, bookingSlug, markInitialPending]
  );

  useEffect(() => {
    const snap = bookingRef.current;
    if (!bookingSlug || !snap?.shiftPattern?.length || isFirstInvited) return;

    const len = snap.shiftPattern.length;
    for (let shiftIndex = 0; shiftIndex < len; shiftIndex++) {
      const initKey = `${bootstrapKey}:${shiftIndex}`;
      if (initDoneRef.current.has(initKey)) continue;

      const pattern = snap.shiftPattern[shiftIndex];
      const sessionId = pattern?.suggestedWorkersSessionId;

      if (sessionId) {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, true);
        fetchClientSuggestedWorkersSession(sessionId)
          .then((res) => {
            applyShiftSlice(shiftIndex, res, { append: false });
            markInitialPending(shiftIndex, false);
          })
          .catch(() => {
            markInitialPending(shiftIndex, false);
            setByShift((prev) => ({
              ...prev,
              [shiftIndex]: {
                ...(prev[shiftIndex] || {}),
                workers: [],
                hasMore: false,
                initialLoaded: true,
                sessionError: true,
              },
            }));
            toastr.error("Could not restore suggested workers session.");
          });
      } else {
        initDoneRef.current.add(initKey);
        markInitialPending(shiftIndex, true);
        fetchSuggestedPage(shiftIndex, { append: false });
      }
    }
    // Intentionally omit `booking` — use bookingRef to avoid re-fetch loops on React Query refetch.
  }, [
    bookingSlug,
    patternsLen,
    bootstrapKey,
    fetchSuggestedPage,
    markInitialPending,
    applyShiftSlice,
    isFirstInvited,
  ]);

  useEffect(() => {
    if (!bookingSlug || !patternsLen || isFirstInvited) return;

    let cancelled = false;
    const sessionId = bookingRef.current?.inviteListWorkerSessionId;

    if (!sessionId) {
      setInviteListByShift(buildEmptyInviteListMap(patternsLen));
      setInviteListSessionError(false);
      return;
    }

    setInviteListByShift(null);
    setInviteListSessionError(false);

    fetchClientInviteListWorkersSession(sessionId)
      .then((res) => {
        if (cancelled) return;
        const data = normalizeClientBookingPayload(res);
        setInviteListByShift(applyShiftsSnapshotToInviteMap(data?.shifts, patternsLen));
        setInviteListSessionError(false);
      })
      .catch(() => {
        if (cancelled) return;
        setInviteListSessionError(true);
        setInviteListByShift(buildEmptyInviteListMap(patternsLen));
        toastr.error("Could not load invite list.");
      });

    return () => {
      cancelled = true;
    };
  }, [bookingSlug, patternsLen, bootstrapKey, inviteListWorkerSessionId, isFirstInvited]);

  useEffect(() => {
    if (!isFirstInvited) return;
    setByShift({});
    setSelected({});
    setLoadMorePending({});
    setInitialPending({});
    setInviteListByShift(buildEmptyInviteListMap(patternsLen));
    setInviteListSessionError(false);
  }, [isFirstInvited, patternsLen]);

  const loadMore = useCallback(
    (shiftIndex) => {
      if (isFirstInvited) return;
      if (!byShiftRef.current[shiftIndex]?.hasMore) return;
      setLoadMorePending((p) => ({ ...p, [shiftIndex]: true }));
      fetchSuggestedPage(shiftIndex, { append: true });
    },
    [fetchSuggestedPage, isFirstInvited]
  );

  const toggleDate = useCallback((shiftIndex, workerSlug, isoDate) => {
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      const dates = new Set(row[workerSlug] || []);
      if (dates.has(isoDate)) dates.delete(isoDate);
      else dates.add(isoDate);
      row[workerSlug] = [...dates];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const selectAllDatesForWorker = useCallback((shiftIndex, workerSlug) => {
    if (!workerSlug) return;
    const workers = byShiftRef.current[shiftIndex]?.workers ?? [];
    const w = workers.find((x) => getWorkerSlug(x) === workerSlug);
    const allDates = w ? getWorkerAvailableDates(w) : [];
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      row[workerSlug] = [...allDates];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const removeWorkerLocal = useCallback((shiftIndex, workerSlug) => {
    setByShift((prev) => {
      const row = prev[shiftIndex];
      if (!row?.workers) return prev;
      return {
        ...prev,
        [shiftIndex]: {
          ...row,
          workers: row.workers.filter((w) => getWorkerSlug(w) !== workerSlug),
        },
      };
    });
    setSelected((prev) => {
      const row = { ...(prev[shiftIndex] || {}) };
      delete row[workerSlug];
      return { ...prev, [shiftIndex]: row };
    });
  }, []);

  const removeInviteListWorkerLocal = useCallback((shiftIndex, workerSlug) => {
    setInviteListByShift((prev) => {
      if (!prev?.[shiftIndex]?.workers?.length) return prev;
      const row = prev[shiftIndex];
      return {
        ...prev,
        [shiftIndex]: {
          ...row,
          workers: row.workers.filter((w) => w.workerSlug !== workerSlug),
        },
      };
    });
  }, []);

  const rejectWorker = useCallback(
    (shiftIndex, workerSlug) => {
      const key = getStep1ActionKey(shiftIndex, workerSlug);
      setPendingRejectKey(key);
      rejectMut.mutate(
        {
          bookingSlug,
          shiftIndex,
          workerSlug,
          rejectScope: "full_shift",
        },
        {
          onSettled: () => {
            setPendingRejectKey((cur) => (cur === key ? null : cur));
          },
          onSuccess: () => {
            removeWorkerLocal(shiftIndex, workerSlug);
            removeInviteListWorkerLocal(shiftIndex, workerSlug);
            toastr.success("Worker removed from suggestions.");
          },
        }
      );
    },
    [bookingSlug, rejectMut, removeInviteListWorkerLocal, removeWorkerLocal]
  );

  const addToInviteList = useCallback(
    (shiftIndex, workerSlug, workerDates) => {
      if (!workerSlug || !workerDates?.length) {
        toastr.warning("Select at least one date to add this worker to the invite list.");
        return;
      }
      const cap = getShiftInviteCap(booking?.shiftPattern?.[shiftIndex]);
      const currentCount = inviteListByShift?.[shiftIndex]?.workers?.length ?? 0;
      if (cap > 0 && currentCount >= cap) {
        toastr.warning(`Shift ${shiftIndex + 1} invite list is full (${currentCount}/${cap}).`);
        return;
      }
      const key = getStep1ActionKey(shiftIndex, workerSlug);
      setPendingAppendKey(key);
      appendMut.mutate(
        {
          bookingSlug,
          workers: [{ shiftIndex, workerSlug, workerDates }],
        },
        {
          onSettled: () => {
            setPendingAppendKey((cur) => (cur === key ? null : cur));
          },
          onSuccess: (res) => {
            const data = normalizeClientBookingPayload(res);
            if (data?.shifts) {
              setInviteListByShift(applyShiftsSnapshotToInviteMap(data.shifts, patternsLen));
            }
            toastr.success("Added to invite list.");
            setSelected((prev) => {
              const row = { ...(prev[shiftIndex] || {}) };
              delete row[workerSlug];
              return { ...prev, [shiftIndex]: row };
            });
          },
        }
      );
    },
    [appendMut, booking?.shiftPattern, bookingSlug, inviteListByShift, patternsLen]
  );

  const removeFromInviteList = useCallback(
    (shiftIndex, workerSlug) => {
      const key = getStep1ActionKey(shiftIndex, workerSlug);
      setPendingRemoveKey(key);
      removeMut.mutate(
        { bookingSlug, shiftIndex, workerSlug },
        {
          onSettled: () => {
            setPendingRemoveKey((cur) => (cur === key ? null : cur));
          },
          onSuccess: (res) => {
            const data = normalizeClientBookingPayload(res);
            if (data && typeof data.shiftIndex === "number") {
              setInviteListByShift((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  [data.shiftIndex]: {
                    workerAlreadyInvited: !!data.workerAlreadyInvited,
                    workers: Array.isArray(data.workers) ? data.workers : [],
                  },
                };
              });
            }
            toastr.success("Removed from invite list.");
          },
        }
      );
    },
    [bookingSlug, removeMut]
  );

  const inviteWorkers = useCallback(() => {
    const hasAnyoneOnList =
      inviteListByShift &&
      Object.values(inviteListByShift).some(
        (row) => (row?.workers?.length ?? 0) > 0
      );
    if (!hasAnyoneOnList) {
      toastr.warning("Add at least one worker to an invite list before sending invites.");
      return;
    }
    inviteMut.mutate(
      { bookingSlug, payload: {} },
      {
        onSuccess: () => {
          toastr.success("Invites sent.");
          // Immediately block Step 1 suggested/invite-list GETs before booking refetch lands.
          setFirstInviteLocked(true);
          setBootstrapKey((k) => k + 1);
          setByShift({});
          setSelected({});
          onAfterInvite?.();
        },
      }
    );
  }, [bookingSlug, inviteListByShift, inviteMut, onAfterInvite]);

  const listBusy =
    appendMut.isPending || removeMut.isPending || rejectMut.isPending;

  const invitedQ = useInvitedWorkerRequestsInfinite(bookingSlug, isFirstInvited);

  const shiftInviteMeta = useMemo(() => {
    const patterns = booking?.shiftPattern ?? [];
    return patterns.map((pattern, shiftIndex) => {
      const cap = getShiftInviteCap(pattern);
      const currentCount = inviteListByShift?.[shiftIndex]?.workers?.length ?? 0;
      return {
        shiftIndex,
        cap,
        currentCount,
        isFull: cap > 0 && currentCount >= cap,
      };
    });
  }, [booking?.shiftPattern, inviteListByShift]);

  return {
    byShift,
    selected,
    inviteListByShift,
    inviteListSessionError,
    loadMore,
    loadMorePending,
    initialPending,
    toggleDate,
    selectAllDatesForWorker,
    addToInviteList,
    removeFromInviteList,
    rejectWorker,
    inviteWorkers,
    rejectMutation: rejectMut,
    inviteMutation: inviteMut,
    appendMutation: appendMut,
    removeMutation: removeMut,
    listBusy,
    pendingAppendKey,
    pendingRejectKey,
    pendingRemoveKey,
    bootstrapKey,
    invitedQ,
    isFirstInvited,
    shiftInviteMeta,
    parseWorkerRequestsPage,
  };
}
